/**
 * ScoreRing — Palantir-style multi-dimension SVG ring gauge.
 *
 * Renders a circular composite gauge:
 *   • Outer 5 arc segments — one per dimension (Influence / Trust /
 *     Innovation / Investment / Collaboration).
 *   • Each segment fills 0-100% along an 70°-arc allocated to it.
 *   • Inner circle shows the composite score in monospace digits.
 *   • Subtle target/cross-hair lines anchor the ring on the canvas.
 *
 * Pure SVG — no canvas, no layout flicker. Fully theme-aware via the
 * `isDark` prop.
 */
import React, { useMemo } from 'react';

const DIMS = [
  { key: 'influence',     label: 'INFL',  color: '#AE52EA' },
  { key: 'trust',         label: 'TRST',  color: '#3B82F6' },
  { key: 'innovation',    label: 'INNV',  color: '#7C3AED' },
  { key: 'investment',    label: 'INVT',  color: '#10B981' },
  { key: 'collaboration', label: 'COLL',  color: '#06B6D4' },
];

function polar(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  const [x1, y1] = polar(cx, cy, r, startDeg);
  const [x2, y2] = polar(cx, cy, r, endDeg);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

export function ScoreRing({
  composite = 0,
  dimensions = {},
  isDark = true,
  size = 220,
  testId = 'shura-score-ring',
}) {
  const cx = size / 2;
  const cy = size / 2;
  const ringR  = size * 0.42;  // outer arc radius
  const innerR = size * 0.30;  // inner score circle radius

  // 5 segments arranged around the ring with small gaps between.
  // Each segment is allocated 360/5 = 72°, leaving 2° gap on each side.
  const segments = useMemo(() => {
    const total = DIMS.length;
    const slice = 360 / total;
    const gap = 4;
    return DIMS.map((d, i) => {
      const start = i * slice - 90 + gap / 2;
      const end   = (i + 1) * slice - 90 - gap / 2;
      const value = Math.max(0, Math.min(100, Number(dimensions[d.key] || 0)));
      return { ...d, start, end, value };
    });
  }, [dimensions]);

  const fg = isDark ? '#FFFFFF' : '#0A0A0A';
  const trackColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(94,31,139,0.10)';
  const accent = isDark ? '#AE52EA' : '#5E1F8B';

  // Composite score colour band — gold-tier scoring without using literal gold.
  const compColor = composite >= 75 ? '#AE52EA' : composite >= 50 ? '#7C3AED' : composite >= 25 ? '#5E1F8B' : '#3B0F60';

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="w-full h-auto"
      role="img"
      aria-label={`Shura composite score ${composite} out of 100`}
      data-testid={testId}
    >
      <defs>
        <radialGradient id="ring-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={compColor} stopOpacity="0.35" />
          <stop offset="60%" stopColor={compColor} stopOpacity="0.08" />
          <stop offset="100%" stopColor={compColor} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cross-hair" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0" />
          <stop offset="50%" stopColor={accent} stopOpacity="0.5" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Cross-hair guides */}
      <line x1="0"        y1={cy}     x2={size}    y2={cy}     stroke="url(#cross-hair)" strokeWidth="0.5" />
      <line x1={cx}       y1="0"      x2={cx}      y2={size}   stroke="url(#cross-hair)" strokeWidth="0.5" />

      {/* Inner glow halo behind the composite score */}
      <circle cx={cx} cy={cy} r={innerR + 8} fill="url(#ring-glow)" />

      {/* Segment tracks (faint) + filled arcs (bright) */}
      {segments.map((s) => {
        const fullArc = arcPath(cx, cy, ringR, s.start, s.end);
        const fillEndDeg = s.start + ((s.end - s.start) * s.value) / 100;
        const fillArc = arcPath(cx, cy, ringR, s.start, fillEndDeg);
        return (
          <g key={s.key}>
            <path d={fullArc} stroke={trackColor} strokeWidth="6" fill="none" strokeLinecap="butt" />
            <path
              d={fillArc}
              stroke={s.color}
              strokeWidth="6"
              fill="none"
              strokeLinecap="butt"
              style={{ filter: `drop-shadow(0 0 6px ${s.color}80)` }}
            />
          </g>
        );
      })}

      {/* Dimension micro-labels — placed just outside each segment midpoint */}
      {segments.map((s) => {
        const mid = (s.start + s.end) / 2;
        const [lx, ly] = polar(cx, cy, ringR + 16, mid);
        return (
          <g key={`${s.key}-label`}>
            <text
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              className="pal-mono"
              style={{ fontSize: 9, letterSpacing: 1.5, fill: s.color, fontWeight: 700 }}
            >
              {s.label}
            </text>
            <text
              x={lx}
              y={ly + 11}
              textAnchor="middle"
              dominantBaseline="middle"
              className="pal-mono"
              style={{ fontSize: 8.5, fill: fg, opacity: 0.7 }}
            >
              {s.value}
            </text>
          </g>
        );
      })}

      {/* Inner composite ring (decorative) */}
      <circle cx={cx} cy={cy} r={innerR} fill="none" stroke={trackColor} strokeWidth="1" />
      <circle cx={cx} cy={cy} r={innerR - 4} fill="none" stroke={accent} strokeWidth="0.5" strokeOpacity="0.45" />

      {/* Composite score readout */}
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        className="pal-mono"
        style={{ fontSize: 9, letterSpacing: 2, fill: accent, fontWeight: 700 }}
      >
        [SHURA]
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        className="pal-mono"
        style={{ fontSize: 36, fill: fg, fontWeight: 700, letterSpacing: -1 }}
      >
        {Number(composite).toFixed(0)}
      </text>
      <text
        x={cx}
        y={cy + 32}
        textAnchor="middle"
        className="pal-mono"
        style={{ fontSize: 8.5, letterSpacing: 2, fill: fg, opacity: 0.55, fontWeight: 600 }}
      >
        / 100
      </text>
    </svg>
  );
}

export default ScoreRing;
